import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

interface PaginationProps {
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
}

const MAX_DOTS = 7;

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  currentPage,
  onPageChange,
  pageSize = 15
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Desktop: numbered page buttons
  const pageNumbers: (number | string)[] = [];
  pageNumbers.push(1);
  const windowStart = Math.max(2, currentPage - 1);
  const windowEnd = Math.min(totalPages - 1, currentPage + 1);
  if (windowStart > 2) pageNumbers.push('...');
  for (let i = windowStart; i <= windowEnd; i++) pageNumbers.push(i);
  if (windowEnd < totalPages - 1) pageNumbers.push('...');
  if (totalPages > 1) pageNumbers.push(totalPages);

  // Mobile: dot indicators (capped at MAX_DOTS)
  const dotPages: number[] = [];
  if (totalPages <= MAX_DOTS) {
    for (let i = 1; i <= totalPages; i++) dotPages.push(i);
  } else {
    // Always show first and last, fill middle around current page
    const middleSlots = MAX_DOTS - 2; // slots between first and last
    let start = Math.max(2, currentPage - Math.floor(middleSlots / 2));
    let end = start + middleSlots - 1;
    if (end >= totalPages) {
      end = totalPages - 1;
      start = end - middleSlots + 1;
    }
    dotPages.push(1);
    for (let i = start; i <= end; i++) dotPages.push(i);
    dotPages.push(totalPages);
  }

  return (
    <div className="py-2 text-center overflow-hidden">
      {/* Desktop: numbered buttons with prev/next */}
      <div className="hidden lg:inline-flex gap-0.5 items-center flex-wrap justify-center max-w-full">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="h-7 w-7"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pageNumbers.map((page, i) =>
          typeof page === 'string' ? (
            <span key={`ellipsis-${i}`} className="px-0.5 text-[13px] text-muted-foreground">...</span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? 'default' : 'outline'}
              onClick={() => onPageChange(page)}
              className={cn(
                "h-7 min-w-[28px] px-1 text-[13px]",
                page === currentPage && "font-bold"
              )}
            >
              {page}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="h-7 w-7"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Mobile: dot indicators */}
      <div className="flex lg:hidden justify-center items-center gap-1.5">
        {dotPages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            aria-label={`Page ${page}`}
            className={cn(
              "rounded-full transition-all",
              page === currentPage
                ? "w-2 h-2 bg-primary"
                : "w-1.5 h-1.5 bg-muted-foreground/30"
            )}
          />
        ))}
      </div>

      <div className="text-[13px] text-muted-foreground mt-1">
        Showing {(startIndex + 1).toLocaleString()}-{endIndex.toLocaleString()} of {totalItems.toLocaleString()}
      </div>
    </div>
  );
};

export default Pagination;
